import { POSITIONS, type DefensePosition } from "./defense.js";

export { POSITIONS } from "./defense.js";

export const NUM_DEF_INNINGS = 9;

export const DEPTH_CHART: Record<DefensePosition, readonly string[]> = {
  P: [],
  C: ["Noah", "Pete", "Brad"],
  "1B": ["Drew", "Ryan T", "Dan", "Erik"],
  "2B": ["Tom", "Dan"],
  SS: ["Gabe", "Gethan"],
  "3B": ["Gethan", "Drew", "Erik"],
  LF: ["Ben", "Branden"],
  LC: ["Tim", "Branden"],
  RC: ["Branden", "Brad", "Pete", "Dan", "Ryan T"],
  RF: ["Brad", "Dan", "Pete", "Ryan T", "Erik"],
};

const MAX_DEPTH = Math.max(
  ...Object.values(DEPTH_CHART).map((players) => players.length),
);

export const FILLERS: ReadonlySet<string> = new Set(["Mike", "Ryan I"]);

export const FILLER_ALLOWED: Record<string, readonly DefensePosition[]> = {
  Mike: ["3B", "SS", "LF", "LC", "RC", "2B", "RF", "P"],
  "Ryan I": ["3B", "SS", "LF", "LC", "RC", "2B", "RF", "P", "1B"],
};

export function chartPrefs(name: string): DefensePosition[] {
  const entries: Array<{ pos: DefensePosition; index: number }> = [];
  POSITIONS.forEach((pos) => {
    const index = (DEPTH_CHART[pos] ?? []).indexOf(name);
    if (index >= 0) entries.push({ pos, index });
  });
  entries.sort(
    (left, right) =>
      left.index - right.index ||
      POSITIONS.indexOf(left.pos) - POSITIONS.indexOf(right.pos),
  );
  return entries.map(({ pos }) => pos);
}

export const SIT_GROUPS = [
  { names: ["Branden", "Tim", "Ben"], max: 1 },
] as const;

export const HARD_ONLY: ReadonlySet<string> = new Set(["Drew"]);

type PlayerId = string | number;

export interface DefensePlayer<Id extends PlayerId = string> {
  playerId: Id;
  name: string;
  positionPreferences: readonly DefensePosition[];
}

export interface DefenseAssignment<Id extends PlayerId = string> {
  assignment: Partial<Record<DefensePosition, Id>>;
  oop: Id[];
  unplaced: Id[];
}

export interface DefenseInning<Id extends PlayerId = string> {
  assignment: Partial<Record<DefensePosition, Id>>;
  sitters: Id[];
  oop: Id[];
}

export function assignPositions<Id extends PlayerId>(
  fielders: readonly DefensePlayer<Id>[],
  pitcherId: Id | null,
): DefenseAssignment<Id> {
  const assignment: Partial<Record<DefensePosition, Id>> = {};
  const oop: Id[] = [];
  if (pitcherId) assignment.P = pitcherId;
  const unassigned = new Set(fielders.map((player) => player.playerId));

  fielders.forEach((player) => {
    if (!HARD_ONLY.has(player.name)) return;
    const position = player.positionPreferences.find(
      (candidate) =>
        !assignment[candidate] && !(candidate === "P" && pitcherId),
    );
    if (position) {
      assignment[position] = player.playerId;
      unassigned.delete(player.playerId);
    }
  });

  for (let depth = 0; depth < MAX_DEPTH; depth += 1) {
    POSITIONS.forEach((position) => {
      if (assignment[position] || (position === "P" && pitcherId)) {
        return;
      }
      const name = DEPTH_CHART[position]?.[depth];
      if (!name) return;
      const player = fielders.find((candidate) => candidate.name === name);
      if (player && unassigned.has(player.playerId)) {
        assignment[position] = player.playerId;
        unassigned.delete(player.playerId);
      }
    });
  }

  fielders.forEach((player) => {
    if (!FILLERS.has(player.name) || !unassigned.has(player.playerId)) return;
    const openSpots = POSITIONS.filter(
      (position) => !assignment[position] && !(position === "P" && pitcherId),
    );
    const position =
      openSpots.find((candidate) =>
        player.positionPreferences.includes(candidate),
      ) ?? openSpots[0];
    if (position) {
      assignment[position] = player.playerId;
      unassigned.delete(player.playerId);
      if (!player.positionPreferences.includes(position)) {
        oop.push(player.playerId);
      }
    }
  });

  [...unassigned].forEach((playerId) => {
    const player = fielders.find(
      (candidate) => candidate.playerId === playerId,
    );
    if (!player) return;
    if (HARD_ONLY.has(player.name)) {
      unassigned.delete(playerId);
      return;
    }
    const openSpots = POSITIONS.filter(
      (position) => !assignment[position] && !(position === "P" && pitcherId),
    );
    const position =
      openSpots.find((candidate) =>
        player.positionPreferences.includes(candidate),
      ) ?? openSpots[0];
    if (position) {
      assignment[position] = playerId;
      unassigned.delete(playerId);
      if (!player.positionPreferences.includes(position)) oop.push(playerId);
    }
  });

  return { assignment, oop, unplaced: [...unassigned] };
}

function* sitCombos<Id>(
  values: readonly Id[],
  count: number,
  start = 0,
  current: Id[] = [],
): Generator<Id[]> {
  if (current.length === count) {
    yield [...current];
    return;
  }
  for (
    let index = start;
    index <= values.length - (count - current.length);
    index += 1
  ) {
    current.push(values[index]!);
    yield* sitCombos(values, count, index + 1, current);
    current.pop();
  }
}

export function generateDefense<Id extends PlayerId>(
  active: readonly DefensePlayer<Id>[],
  pitcherId: Id | null,
): DefenseInning<Id>[] {
  const sitCounts = new Map(active.map((player) => [player.playerId, 0]));
  const nonPitchers = active.filter((player) => player.playerId !== pitcherId);
  const sitsPerInning = Math.max(0, active.length - POSITIONS.length);
  const nameOf = (playerId: Id) =>
    active.find((player) => player.playerId === playerId)?.name ?? "";
  const innings: DefenseInning<Id>[] = [];
  let previousSitters = new Set<Id>();

  for (let inning = 0; inning < NUM_DEF_INNINGS; inning += 1) {
    let best: (DefenseAssignment<Id> & { sitters: Id[] }) | null = null;
    let bestScore = Infinity;
    if (sitsPerInning === 0) {
      best = { sitters: [], ...assignPositions(nonPitchers, pitcherId) };
    } else {
      const sorted = [...nonPitchers].sort(
        (left, right) =>
          (sitCounts.get(left.playerId) ?? 0) -
            (sitCounts.get(right.playerId) ?? 0) ||
          ((nonPitchers.indexOf(left) + inning) % nonPitchers.length) -
            ((nonPitchers.indexOf(right) + inning) % nonPitchers.length),
      );
      const threshold = sitCounts.get(sorted[sitsPerInning - 1]!.playerId) ?? 0;
      const mustSit = sorted
        .filter((player) => (sitCounts.get(player.playerId) ?? 0) < threshold)
        .map((player) => player.playerId);
      const tied = sorted
        .filter((player) => (sitCounts.get(player.playerId) ?? 0) === threshold)
        .map((player) => player.playerId);
      const needed = sitsPerInning - mustSit.length;

      for (const combination of sitCombos(tied, needed)) {
        const sitters = [...mustSit, ...combination];
        const violatesConstraint = SIT_GROUPS.some(
          (group) =>
            sitters.filter((playerId) =>
              group.names.some((name) => name === nameOf(playerId)),
            ).length > group.max,
        );
        if (violatesConstraint) continue;
        const fielders = nonPitchers.filter(
          (player) => !sitters.includes(player.playerId),
        );
        const assignment = assignPositions(fielders, pitcherId);
        const backToBack = sitters.filter((playerId) =>
          previousSitters.has(playerId),
        ).length;
        const score =
          assignment.unplaced.length * 10000 +
          assignment.oop.length * 100 +
          backToBack;
        if (score < bestScore) {
          best = { sitters, ...assignment };
          bestScore = score;
          if (score === 0) break;
        }
      }
      if (!best) {
        const sitters = sorted
          .slice(0, sitsPerInning)
          .map((player) => player.playerId);
        const fielders = nonPitchers.filter(
          (player) => !sitters.includes(player.playerId),
        );
        best = { sitters, ...assignPositions(fielders, pitcherId) };
      }
    }
    best.sitters.forEach((playerId) =>
      sitCounts.set(playerId, (sitCounts.get(playerId) ?? 0) + 1),
    );
    previousSitters = new Set(best.sitters);
    innings.push({
      assignment: best.assignment,
      sitters: best.sitters,
      oop: best.oop,
    });
  }
  return innings;
}
